import { useMemo } from 'react';
import { checkAggregateACL, isWeaveUser } from '@frontend/auth-helpers';
import { CALL_INTEL_TASK_SETTINGS_FF, useCallIntelSubscription } from '@frontend/call-intel-core';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { TASK_SETTINGS_PERMISSION } from '../constants';

export const useHasCallIntelTaskSettingAccess = (locationIds?: string[]) => {
  const { selectedLocationIds: scopedLocationIds } = useAppScopeStore();
  const selectedLocationIds = locationIds ?? scopedLocationIds;

  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const isTaskSettingsFFEnabled = getFeatureFlagValue(CALL_INTEL_TASK_SETTINGS_FF);
  const { callIntelProPlanLocationIds: activeLocationIds, isLoading: isLoadingActiveLocationIds } =
    useCallIntelSubscription({
      locationIds: selectedLocationIds,
    });

  const { aclValuesByLocationId } = checkAggregateACL(activeLocationIds, TASK_SETTINGS_PERMISSION, 'all');

  const totalLocationsCount = selectedLocationIds.length;
  const activeLocationsCount = activeLocationIds.length;

  const { authorizedLocationIds, isAuthorizedOnAllLocations, authorizedLocationMap } = useMemo(() => {
    const _authorizedLocationMap = activeLocationIds.reduce<Record<string, boolean>>((map, locationId) => {
      map[locationId] = isWeaveUser() || !!aclValuesByLocationId[locationId];
      return map;
    }, {});

    const _authorizedLocationIds = activeLocationIds.filter(
      (locationId) => _authorizedLocationMap[locationId] === true
    );

    return {
      authorizedLocationIds: _authorizedLocationIds,
      authorizedLocationMap: _authorizedLocationMap,
      isAuthorizedOnAllLocations: _authorizedLocationIds.length === activeLocationIds.length,
    };
  }, [aclValuesByLocationId, activeLocationIds]);

  const hasAccess = !!isTaskSettingsFFEnabled && !isLoadingActiveLocationIds && authorizedLocationIds.length > 0;

  return {
    isLoading: isLoadingActiveLocationIds,
    activeLocationIds,
    totalLocationsCount,
    activeLocationsCount,
    authorizedLocationIds,
    authorizedLocationMap,
    isAuthorizedOnAllLocations,
    hasAccess,
  };
};
